import { useCallback, useEffect, useRef, useState } from "react";
import { useReservationInvalidation } from "../../app/hooks/useFamilyWebSocket";
import { Icon } from "../../components/Icon";
import {
  cancelReservation,
  getMyReservations,
  purchaseReservation,
  restoreReservation,
} from "../../lib/api-reservation";
import type {
  MyReservation,
  ReservationMutation,
} from "../../types/reservation";
import {
  ReservationActionConfirm,
  type ReservationConfirmationAction,
} from "../wish/ReservationActionConfirm";

type State = { status: "loading" } | { status: "error" } | { status: "loaded"; items: MyReservation[] };
type Notice = { kind: "success" | "error"; message: string };

interface SelectedAction {
  item: MyReservation;
  action: Extract<ReservationConfirmationAction, "cancel" | "purchase" | "restore" | "release">;
}

function validProductURL(value?: string): string | null {
  if (!value) return null;
  try {
    const url = new URL(value);
    return url.protocol === "http:" || url.protocol === "https:" ? url.toString() : null;
  } catch {
    return null;
  }
}

const money = new Intl.NumberFormat("ru-RU", { style: "currency", currency: "RUB", maximumFractionDigits: 0 });

function ReservationCard({
  item,
  pending,
  onAction,
}: {
  item: MyReservation;
  pending: boolean;
  onAction: (item: MyReservation, action: SelectedAction["action"]) => void;
}) {
  const productURL = validProductURL(item.wish.url);
  const purchased = item.status === "purchased";
  return (
    <article className="overflow-hidden rounded-[26px] bg-white shadow-[0_14px_36px_rgba(35,33,25,0.07)] sm:flex">
      <div className="flex h-48 items-center justify-center bg-[#EEECE4] sm:h-auto sm:w-44 sm:shrink-0">
        {item.wish.image_url ? <img src={item.wish.image_url} alt="" className="h-full w-full object-cover" /> : <Icon name="gift" size={42} className="text-[#AAA89E]" />}
      </div>
      <div className="flex min-w-0 flex-1 flex-col p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#77776F]">Для {item.recipient.name}</p>
            <h2 className="mt-1 text-xl font-extrabold tracking-[-0.035em] text-[#171714]">{item.wish.title}</h2>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${purchased ? "bg-[#E5F4EA] text-[#287A45]" : "bg-[#FFF0DF] text-[#C55E00]"}`}>
            {purchased ? "Куплено" : "Забронировано"}
          </span>
        </div>
        {item.event && <p className="mt-3 text-sm text-[#77776F]">{item.event.title} · {new Date(`${item.event.date}T00:00:00`).toLocaleDateString("ru-RU", { day: "numeric", month: "long" })}</p>}
        <div className="mt-auto flex flex-wrap items-end justify-between gap-4 pt-5">
          <span className="text-lg font-extrabold text-[#171714]">{item.wish.price == null ? "Цена не указана" : money.format(item.wish.price)}</span>
          {productURL && <a href={productURL} target="_blank" rel="noreferrer" className="rounded-full bg-[#171714] px-5 py-2.5 text-sm font-semibold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714]">К товару</a>}
        </div>
        <div className="mt-4 grid grid-cols-1 gap-2 border-t border-[#E3E1D9] pt-4 min-[420px]:grid-cols-2" aria-busy={pending}>
          {purchased ? (
            <>
              <button
                type="button"
                disabled={pending}
                onClick={() => onAction(item, "restore")}
                className="min-h-11 cursor-pointer rounded-xl bg-[#F2F1EC] px-4 py-2 text-sm font-bold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-wait disabled:opacity-60"
              >
                Вернуть в брони
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => onAction(item, "release")}
                className="min-h-11 cursor-pointer rounded-xl bg-[#FEF2F2] px-4 py-2 text-sm font-bold text-[#B91C1C] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B91C1C] disabled:cursor-wait disabled:opacity-60"
              >
                Освободить подарок
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                disabled={pending}
                onClick={() => onAction(item, "cancel")}
                className="min-h-11 cursor-pointer rounded-xl bg-[#F2F1EC] px-4 py-2 text-sm font-bold text-[#171714] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#171714] disabled:cursor-wait disabled:opacity-60"
              >
                Отменить бронь
              </button>
              <button
                type="button"
                disabled={pending}
                onClick={() => onAction(item, "purchase")}
                className="min-h-11 cursor-pointer rounded-xl bg-[#FF7A00] px-4 py-2 text-sm font-bold text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#FF7A00] disabled:cursor-wait disabled:bg-[#FDBA74]"
              >
                Отметить купленным
              </button>
            </>
          )}
        </div>
      </div>
    </article>
  );
}

export function MyReservationsPage() {
  const [state, setState] = useState<State>({ status: "loading" });
  const [selectedAction, setSelectedAction] = useState<SelectedAction | null>(null);
  const [mutationPending, setMutationPending] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);
  const requestIDRef = useRef(0);

  const fetchReservations = useCallback(async (showLoading: boolean) => {
    const requestID = ++requestIDRef.current;
    if (showLoading) setState({ status: "loading" });
    try {
      const items = await getMyReservations();
      if (requestID === requestIDRef.current) {
        setState({ status: "loaded", items });
      }
    } catch (error) {
      if (showLoading && requestID === requestIDRef.current) {
        setState({ status: "error" });
      }
      if (showLoading) return;
      throw error;
    }
  }, []);

  const load = useCallback(() => fetchReservations(true), [fetchReservations]);
  const refetch = useCallback(() => fetchReservations(false), [fetchReservations]);

  useReservationInvalidation(() => {
    void refetch().catch(() => undefined);
  });

  const applyMutation = (mutation: ReservationMutation) => {
    const status = mutation.status;
    setState((current) => {
      if (current.status !== "loaded") return current;
      if (status === "cancelled") {
        return { status: "loaded", items: current.items.filter((item) => item.id !== mutation.reservation_id) };
      }
      return {
        status: "loaded",
        items: current.items.map((item) =>
          item.id === mutation.reservation_id
            ? {
                ...item,
                status,
                purchased_at: status === "purchased"
                  ? mutation.purchased_at ?? item.purchased_at
                  : null,
              }
            : item,
        ),
      };
    });
  };

  const openAction = (item: MyReservation, action: SelectedAction["action"]) => {
    if (
      (item.status === "reserved" && action !== "cancel" && action !== "purchase") ||
      (item.status === "purchased" && action !== "restore" && action !== "release")
    ) return;
    setNotice(null);
    setSelectedAction({ item, action });
  };

  const confirmAction = async () => {
    if (!selectedAction) return;
    const { item, action } = selectedAction;
    setMutationPending(true);
    setNotice(null);
    try {
      const mutation = action === "cancel" || action === "release"
        ? await cancelReservation(item.id)
        : action === "restore"
          ? await restoreReservation(item.id)
          : await purchaseReservation(item.id);
      applyMutation(mutation);
      setNotice({
        kind: "success",
        message: action === "cancel"
          ? "Бронь отменена."
          : action === "purchase"
            ? "Подарок отмечен купленным."
            : action === "restore"
              ? "Подарок возвращён в брони."
              : "Подарок освобождён.",
      });
      try {
        await refetch();
      } catch {
        setNotice({ kind: "error", message: "Изменение сохранено, но обновить список не удалось. Попробуйте ещё раз." });
      }
      setSelectedAction(null);
    } catch {
      try {
        await refetch();
        setNotice({ kind: "error", message: "Не удалось выполнить действие. Список обновлён — проверьте актуальный статус." });
      } catch {
        setNotice({ kind: "error", message: "Не удалось выполнить действие и обновить список. Попробуйте ещё раз." });
      }
      setSelectedAction(null);
    } finally {
      setMutationPending(false);
    }
  };

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="flex flex-1 flex-col px-5 pb-8 pt-7 sm:px-8 lg:px-10 lg:py-10 xl:px-14">
      <header className="mb-8">
        <p className="mb-1 text-[11px] font-bold uppercase tracking-[0.18em] text-[#FF7A00]">Подарки, за которые отвечаю я</p>
        <h1 className="text-[32px] font-extrabold leading-none tracking-[-0.055em] text-[#171714] lg:text-[46px]">Мои брони</h1>
      </header>
      {notice && (
        <div
          role={notice.kind === "error" ? "alert" : "status"}
          className={`mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm font-semibold ${notice.kind === "error" ? "border-[#F2B8B5] bg-[#FEF2F2] text-[#B91C1C]" : "border-[#F4C48F] bg-[#FFF7ED] text-[#9A4900]"}`}
        >
          <span>{notice.message}</span>
          {notice.kind === "error" && (
            <button type="button" onClick={() => void load()} className="cursor-pointer underline underline-offset-2">
              Обновить список
            </button>
          )}
        </div>
      )}
      {state.status === "loading" && <div className="flex flex-1 items-center justify-center py-20" aria-label="Загрузка броней"><div className="h-9 w-9 animate-spin rounded-full border-[3px] border-[#FF7A00] border-t-transparent" /></div>}
      {state.status === "error" && <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center"><h2 className="text-2xl font-extrabold">Не удалось загрузить брони</h2><p className="mt-2 text-sm text-[#77776F]">Проверьте подключение и попробуйте снова.</p><button type="button" onClick={() => void load()} className="mt-6 rounded-full bg-[#171714] px-7 py-3 text-sm font-semibold text-white">Повторить</button></div>}
      {state.status === "loaded" && state.items.length === 0 && <div className="flex flex-1 flex-col items-center justify-center rounded-[28px] bg-white px-7 py-16 text-center"><div className="flex h-20 w-20 items-center justify-center rounded-[26px] bg-[#FFF0DF] text-[#FF7A00]"><Icon name="calendar" size={36} /></div><h2 className="mt-6 text-2xl font-extrabold">Броней пока нет</h2><p className="mt-2 max-w-sm text-sm leading-6 text-[#77776F]">Забронированные подарки появятся здесь и сохранятся после перезагрузки.</p></div>}
      {state.status === "loaded" && state.items.length > 0 && <div className="grid grid-cols-1 gap-4 2xl:grid-cols-2">{state.items.map((item) => <ReservationCard key={item.id} item={item} pending={mutationPending && selectedAction?.item.id === item.id} onAction={openAction} />)}</div>}
      {selectedAction && (
        <ReservationActionConfirm
          action={selectedAction.action}
          wishTitle={selectedAction.item.wish.title}
          pending={mutationPending}
          error={null}
          onClose={() => setSelectedAction(null)}
          onConfirm={() => void confirmAction()}
        />
      )}
    </div>
  );
}
